import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import { MD } from "./mdTheme";

/** Shared override so SAR/AED icons match the surrounding value size (like ExDashboard). */
const currencyMatchValueSx = {
  "& .currency-symbol": {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "1em !important",
    fontWeight: "inherit",
    lineHeight: 1,
    m: 0,
    ml: "4px",
    mr: 0,
    opacity: 1,
    verticalAlign: "baseline",
  },
  "& .currency-symbol .icon-saudi_riyal, & .currency-symbol .dirham-symbol": {
    display: "inline-block",
    fontSize: "1em !important",
    fontWeight: "inherit",
    lineHeight: 1,
    verticalAlign: "baseline",
  },
  "& .currency-symbol .icon-saudi_riyal::before": {
    fontSize: "1em !important",
    verticalAlign: "baseline",
  },
};

/** Compact muted currency mark for card eyebrows / titles (ExDashboard style). */
export const MdCurrencyHint = ({ currency, sx = {} }) => {
  if (!currency) return null;
  return (
    <Box
      component="span"
      sx={{
        color: MD.muted,
        display: "inline-flex",
        alignItems: "center",
        fontFamily: MD.fontUi,
        fontSize: "0.95em",
        fontWeight: 600,
        letterSpacing: 0,
        ml: 0.35,
        textTransform: "none",
        verticalAlign: "middle",
        ...currencyMatchValueSx,
        ...sx,
      }}
    >
      <CurrencySymbol currency={currency} withParentheses={false} />
    </Box>
  );
};

/**
 * Money + currency symbol sized to match the value (overrides global .currency-symbol 1.5em).
 */
export const MdMoneyValue = ({
  amount,
  currency,
  fontSize = 22,
  fontWeight = 700,
  color = "inherit",
  showTooltip = true,
  compact = false,
  sx = {},
}) => {
  const num = Number(amount) || 0;
  const display = compact ? nFormatter(num, 1) : nFormatter(num, 2);
  const full = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const node = (
    <Box
      component="span"
      sx={{
        alignItems: "baseline",
        color,
        display: "inline-flex",
        flexWrap: "nowrap",
        fontFamily: MD.fontHead,
        fontSize,
        fontVariantNumeric: "tabular-nums",
        fontWeight,
        gap: 0,
        lineHeight: 1.15,
        maxWidth: "100%",
        minWidth: 0,
        ...currencyMatchValueSx,
        ...sx,
      }}
    >
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {display}
      </Box>
      {currency ? (
        <CurrencySymbol currency={currency} withParentheses={false} />
      ) : null}
    </Box>
  );

  if (!showTooltip) return node;

  return (
    <Tooltip
      title={
        <Typography sx={{ fontSize: 13 }}>
          {full}
          {currency ? ` ${currency}` : ""}
        </Typography>
      }
      placement="bottom-start"
    >
      {node}
    </Tooltip>
  );
};

export default MdMoneyValue;
